<template>
  <v-card
    :height="widgetHeight"
    :min-width="widgetWidth"
    class="ma-0 pa-0"
    :loading="loading"
  >
    <v-card-title v-if="!loading" class="pa-0 ma-0">
      <widget-header
        :title="title"
        color="blue"
        icon="mdi-chart-timeline-variant"
      >
        <template slot="actions">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="refresh"
                >mdi-refresh</v-icon
              >
            </template>
            <span>{{ $t("Refresh") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="showSettings"
                >mdi-cog</v-icon
              >
            </template>
            <span>{{ $t("Settings") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="removeWidget"
                >mdi-delete</v-icon
              >
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </template>
      </widget-header>
    </v-card-title>
    <v-card-text class="pa-0 ma-0">
      <div
        :style="{
          'max-height': contentHeight + 'px',
          'max-width': contentWidth + 'px',
        }"
        class="overflow-y-auto ma-1 py-1"
      >
        <apexchart
          id="line-chart"
          v-if="!loading"
          type="line"
          :options="chartOptions"
          :series="series"
          :height="contentHeight - 30"
          class="ma-0 pa-0"
        ></apexchart>
      </div>
      <WidgetSettingsModal :ref="modalKey" @on-save="onSettingsSave" />
    </v-card-text>
  </v-card>
</template>

<script>
import WidgetService from '@/services/WidgetService';
import WidgetHeader from '@/components/widgets/WidgetHeader';
import WidgetSettingsModal from '@/components/widgets/WidgetSettingsModal';
import WidgetSettings from '@/ecm/models/WidgetSettings';
import VueApexCharts from 'vue-apexcharts';
import { mapGetters } from 'vuex';
import { EventBus } from '@/eventBus';
import { createLegacyHubConnection } from '@/plugins/signalr';

export default {
    components: { WidgetHeader, WidgetSettingsModal, apexchart: VueApexCharts },
    name: 'WidgetLineChart',
    props: {
        widgetId: {
            type: Number,
            default: 0,
        },
        id: {
            type: Number,
            default: 0,
        },
        enableEdit: {
            type: Boolean,
            default: false,
        },
        height: { type: Number, default: 0 },
        width: { type: Number, default: 0 },
        settings: { type: Object },
    },
    mounted() {
        this.widgetHeight = this.height;
        this.widgetWidth = this.width;
        this.settingsModel = new WidgetSettings(this.widgetId, this.settings);
        this.refresh();

        this.connection = createLegacyHubConnection(this.apiUrl, this.token);
        this.notificationHub = this.connection.createHubProxy('notificationHub');
        const self = this;

        this.onRefresh = (notifications) => {
            const now = Date.now();
            const timeSinceLastMessage = now - self.lastMessageTime;

            // Clear any existing timeout
            if (self.refreshTimeout) {
                clearTimeout(self.refreshTimeout);
                self.refreshTimeout = null;
            }

            // Determine if messages are coming rapidly
            if (timeSinceLastMessage < self.rapidThreshold && self.lastMessageTime > 0) {
                // Messages are rapid - increase the delay
                self.messageCount++;
            } else {
                // Messages have slowed down or this is the first message
                self.messageCount = 1;
            }

            self.lastMessageTime = now;

            // Calculate adaptive delay based on message frequency
            // The more messages in quick succession, the longer we wait
            let delay = self.baseDelay;
            if (self.messageCount > 1) {
                // Exponential backoff: 1s, 2s, 4s, 8s, up to maxDelay
                delay = Math.min(
                    self.baseDelay * Math.pow(2, self.messageCount - 1),
                    self.maxDelay
                );
            }

            // Schedule the refresh
            self.refreshTimeout = setTimeout(() => {
                self.refresh();
                // Reset the message count after refresh
                self.messageCount = 0;
                self.refreshTimeout = null;
            }, delay);
        };

        this.notificationHub.off('refresh', this.onRefresh);
        this.notificationHub.on('refresh', this.onRefresh);

        this.onReconnected = () => { self.refresh(); };
        this.onDisconnected = () => { self.tryReconnect(); };
        this.onError = (ex) => { console.error('SignalR error:', ex); };

        this.connection.reconnected(this.onReconnected);
        this.connection.disconnected(this.onDisconnected);
        this.connection.error(this.onError);

        this.connection.start();
    },
    beforeDestroy() {
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }

        if (this.notificationHub && this.connection) {
            if (this.onRefresh) this.notificationHub.off('refresh');

            const $c = $(this.connection);

            $c.off('onReconnect');
            $c.off('onDisconnect');
            $c.off('onError');

            this.connection.stop();
            this.connection = null;
        }
    },
    data() {
        return {
            // SignalR
            connection: null,
            notificationHub: null,
            onRefresh: null,
            onReconnecting: null,
            onDisconnected: null,
            onError: null,
            signalRIsTryingToReconnect: false,
            // Adaptive refresh control
            refreshTimeout: null,
            messageCount: 0,
            lastMessageTime: 0,
            baseDelay: 1000, // Base delay: 1 second
            maxDelay: 10000, // Maximum delay: 10 seconds
            rapidThreshold: 500, // If messages come within 500ms, consider it rapid

            title: '',
            loading: false,
            headerHeight: 66,
            widgetHeight: 0,
            widgetWidth: 0,
            settingsModel: new WidgetSettings(),
            modalKey: 'modalSettingsKey',
            chartOptions: {
                chart: {
                    height: this.contentHeight,
                    type: 'line',
                    zoom: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: true,
                },
                tooltip: {
                    enabled: false,
                },
                toolbar: {
                    show: false,
                },
                colors: ['#00cae3'],
                stroke: {
                    curve: 'straight',
                },
                xaxis: {
                    categories: [],
                },
                yaxis: {
                    min: 0,
                    forceNiceScale: true,
                },
            },
            series: [],
        };
    },
    computed: {
        ...mapGetters({
            user: 'account/user',
            apiUrl: 'account/apiUrl',
            token: 'account/token',
            userContentTypes: 'account/userContentTypes',
        }),
        modalSettings() {
            return this.$refs[this.modalKey];
        },
        contentHeight() {
            return this.widgetHeight - this.headerHeight;
        },
        contentWidth() {
            return this.widgetWidth - 20;
        },
    },
    methods: {
        resize(height, width) {
            this.widgetHeight = height;
            this.widgetWidth = width;
            this.refresh();
        },
        refresh() {
            const params = this.getSettings();

            this.loading = true;

            WidgetService.getData(params).then((data) => {
                this.title = this.$t('WidgetLoadedDocumentTitle');

                if (data.length > 0) {
                    this.title += `: ${data[0].Total}`;
                    const series = [];
                    const labels = [];

                    data.forEach((element) => {
                        series.push(element.SeriesValueY_AsInt);
                        labels.push(element.SeriesValueX_AsString);
                    });

                    this.chartOptions.xaxis.categories = labels;
                    this.series = [{ name: this.$t('Documents'), data: series }];
                }

                this.loading = false;
            });
        },
        getSettings() {
            return {
                WidgetID: this.widgetId,
                Id: this.id,
                Settings: this.settingsModel.toDto(),
            };
        },
        showSettings() {
            this.modalSettings.show(
                this.id,
                this.widgetId,
                this.settingsModel.toDto()
            );
        },
        onSettingsSave(dto) {
            this.settingsModel = new WidgetSettings(this.widgetId, dto);
            this.refresh();
        },
        removeWidget() {
            this.$emit('on-remove-widget', this.id);
        },
        tryReconnect() {
            if (this.signalRIsTryingToReconnect)
                return;

            let attempts = 0;
            const maxAttempts = 5;
            const self = this;
            this.signalRIsTryingToReconnect = true;

            function reconnect() {
                if (attempts < maxAttempts) {
                    self.connection.start().done(() => {
                        self.signalRIsTryingToReconnect = false;
                        EventBus.$emit('connection-status', null);
                    }).fail(() => {
                        attempts++;
                        setTimeout(reconnect, 5000); // Retry after 5 seconds
                    });
                } else {
                    console.log('Max reconnection attempts reached. Could not reconnect to SignalR.');
                }
            }

            reconnect();
        }
    },
};
</script>
